import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  addNewSignInRequest,
  signInCheckerRequest,
} from "../../services/APIService";

export interface FormData {
  location: string;
  email: string;
  password: string;
  confirmPassword: string;
}

export interface Errors {
  location?: string;
  email?: string;
  password?: string;
  confirmPassword?: string;
}

export const useSignInVM = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState<FormData>({
    location: "",
    email: "",
    password: "",
    confirmPassword: "",
  });

  const [errors, setErrors] = useState<Errors>({});
  const [showAGB, setShowAGB] = useState<boolean>(false);
  const [showDSGVO, setShowDSGVO] = useState<boolean>(false);
  const [showUse, setShowUse] = useState<boolean>(false);
  const [passwordVisible, setPasswordVisible] = useState<boolean>(false);
  const [passwordVisibleConfirmed, setPasswordVisibleConfirmed] = useState<boolean>(false);

  const checkExistSQLEntrie = async (columnName: string, columnValue: string): Promise<boolean> => {
    try {
      const data = await signInCheckerRequest(columnName, columnValue);
      return data.message !== "OK"; // true = Eintrag existiert bereits
    } catch (error) {
      console.error("Fehler beim Prüfen des Eintrags:", error);
      return true;
    }
  };

  const createNewSignIn = async (place: string, password: string, mail: string): Promise<boolean> => {
    try {
      const data = await addNewSignInRequest(place, password, mail);
      return data.message === "OK";
    } catch (error) {
      console.error("Fehler bei der Registrierung:", error);
      return false;
    }
  };

  const validateForm = async (): Promise<boolean> => {
    const newErrors: Errors = {};

    // Ort prüfen
    if (!formData.location.trim()) {
      newErrors.location = "Bitte Ort eingeben";
    } else {
      const regex = /^[a-zA-Z0-9\s]*$/;
      if (!regex.test(formData.location)) {
        newErrors.location = "Sonderzeichen sind im Ort nicht erlaubt";
      } else {
        const stringWithoutSpaces = formData.location.replace(/\s+/g, "");
        const exists = await checkExistSQLEntrie("login", stringWithoutSpaces);
        if (exists) {
          newErrors.location = "Ort wird bereits verwendet";
        }
      }
    }

    // E-Mail prüfen
    if (!formData.email.trim()) {
      newErrors.email = "Bitte E-Mail eingeben";
    } else {
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailPattern.test(formData.email)) {
        newErrors.email = "Bitte eine gültige E-Mail Adresse eingeben";
      } else {
        const existsMail = await checkExistSQLEntrie("mail", formData.email);
        if (existsMail) {
          newErrors.email = "E-Mail Adresse wird bereits verwendet";
        }
      }
    }

    // Passwort prüfen
    if (!formData.password.trim()) {
      newErrors.password = "Bitte Passwort eingeben";
    } else if (formData.password.length < 8) {
      newErrors.password = "Passwort muss mindestens 8 Zeichen haben";
    }

    // Passwort-Bestätigung prüfen
    if (!formData.confirmPassword.trim()) {
      newErrors.confirmPassword = "Bitte Passwort bestätigen";
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Passwörter stimmen nicht überein";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSubmit = async () => {
    const isValid = await validateForm();
    if (isValid) {
      const success = await createNewSignIn(
        formData.location,
        formData.password,
        formData.email
      );
      if (success) {
        navigate("/TestTime");
      }
    }
  };

  return {
    state: {
      formData,
      errors,
      showAGB,
      showDSGVO,
      showUse,
      passwordVisible,
      passwordVisibleConfirmed,
    },
    actions: {
      handleChange,
      handleSubmit,
      setShowAGB,
      setShowDSGVO,
      setShowUse,
      togglePasswordVisibility: () => setPasswordVisible((prev) => !prev),
      togglePasswordVisibilityConfirmed: () =>
        setPasswordVisibleConfirmed((prev) => !prev),
    },
  };
};